import { supabase } from '../lib/supabaseClient';
import type { PermissionAction, PermissionResource, UUID } from '../types/domain';

/**
 * Calls the real `fn_check_permission(resource, action, branch_id)` RPC
 * (migration 0032) — the single server-side authorization gate. This module
 * never reimplements the role -> permission matrix in JavaScript; every
 * answer comes from the database. A frontend hiding a button based on this
 * result is UX only — the permission-gated RPC functions in
 * `api/purchasing.ts` / `api/masterData.ts` re-check the exact same gate
 * server-side before touching any data, so there is no way to bypass this by
 * calling the RPC directly with dev tools open.
 *
 * p_branch_id = null means "does the user have this permission in ANY
 * branch" (see fn_check_permission's own branch-scope semantics: a null
 * caller-side branch matches any role assignment, tenant-wide or specific).
 * This is what nav-level visibility checks want. Branch-specific enforcement
 * happens again for real inside each mutation RPC once a specific document's
 * branch_id is known.
 *
 * STATUS: IMPLEMENTED_UNVERIFIED — see docs/data-dictionary.md §8k.
 * fn_check_permission itself is DEV_TESTED (test13 §B-§H, 2026-08-15).
 */
export async function checkPermission(
  resource: PermissionResource,
  action: PermissionAction,
  branchId: UUID | null = null
): Promise<boolean> {
  const { data, error } = await supabase.rpc('fn_check_permission', {
    p_resource: resource,
    p_action: action,
    p_branch_id: branchId,
  });
  if (error) {
    // Fail-closed: a network/RPC error must never be interpreted as "allowed".
    // eslint-disable-next-line no-console
    console.error(`fn_check_permission(${resource}, ${action}) failed:`, error.message);
    return false;
  }
  return data === true;
}

/**
 * The vertical-slice-1 permission set the app's navigation and page-level
 * gating need, exactly matching the catalog seeded by migration 0032. This
 * list is deliberately explicit (not derived) so it is obvious, reading this
 * file alone, exactly which checks the frontend prefetches — and so adding a
 * later slice's resources is a one-line addition here, not a hidden
 * assumption elsewhere.
 */
export const VERTICAL_SLICE_1_PERMISSION_CHECKS: Array<{ resource: PermissionResource; action: PermissionAction }> = [
  { resource: 'item', action: 'read' },
  { resource: 'item', action: 'create' },
  { resource: 'supplier', action: 'read' },
  { resource: 'supplier', action: 'create' },
  { resource: 'purchase_request', action: 'read' },
  { resource: 'purchase_request', action: 'create' },
  { resource: 'purchase_request', action: 'approve' },
  { resource: 'purchase_order', action: 'read' },
  { resource: 'purchase_order', action: 'create' },
  { resource: 'goods_receipt', action: 'read' },
  { resource: 'goods_receipt', action: 'create' },
  { resource: 'goods_receipt', action: 'post' },
  { resource: 'stock_ledger', action: 'read' },
  { resource: 'stock_balance', action: 'read' },
  { resource: 'stock_count', action: 'read' },
  { resource: 'employee', action: 'read' },
  { resource: 'attendance_event', action: 'read' },
  { resource: 'attendance_event', action: 'correct' },
  { resource: 'leave_request', action: 'read' },
  { resource: 'shift_swap', action: 'read' },
  { resource: 'disciplinary_action', action: 'read' },
  { resource: 'shift', action: 'read' },
  { resource: 'shift', action: 'create' },
  { resource: 'schedule', action: 'read' },
  { resource: 'schedule', action: 'create' },
  { resource: 'schedule', action: 'update' },
  { resource: 'accounting_period', action: 'read' },
  { resource: 'accounting_period', action: 'create' },
  { resource: 'accounting_period', action: 'close' },
  { resource: 'chart_of_account', action: 'read' },
  { resource: 'chart_of_account', action: 'create' },
  { resource: 'journal_entry', action: 'read' },
  { resource: 'journal_entry', action: 'post' },
  { resource: 'journal_entry', action: 'reverse' },
  { resource: 'payroll_period', action: 'read' },
  { resource: 'payroll_period', action: 'create' },
  { resource: 'payroll_period', action: 'lock' },
  { resource: 'payroll_fact', action: 'read' },
  { resource: 'payroll_fact', action: 'create' },
  { resource: 'payroll_fact', action: 'approve' },
  { resource: 'payroll_run', action: 'read' },
  { resource: 'payroll_run', action: 'create' },
  { resource: 'payroll_run', action: 'approve' },
  { resource: 'payroll_run', action: 'post' },
  { resource: 'ap_invoice', action: 'read' },
  { resource: 'ap_invoice', action: 'create' },
  { resource: 'ap_invoice', action: 'approve' },
  { resource: 'ap_invoice', action: 'post' },
  { resource: 'payment_out', action: 'read' },
  { resource: 'payment_out', action: 'create' },
  { resource: 'payment_out', action: 'approve' },
  { resource: 'payment_out', action: 'post' },
  { resource: 'payroll', action: 'read' },
  { resource: 'stock_count', action: 'create' },
  { resource: 'stock_count', action: 'post' },
  { resource: 'transfer', action: 'read' },
  { resource: 'transfer', action: 'create' },
  { resource: 'transfer', action: 'post' },
  { resource: 'waste', action: 'read' },
  { resource: 'waste', action: 'create' },
  { resource: 'waste', action: 'post' },
  { resource: 'expiry_lot', action: 'read' },
  { resource: 'expiry_lot', action: 'create' },
  // FASE 11 (migration 0048): Assets/Maintenance + CRM Foundation.
  { resource: 'asset', action: 'read' },
  { resource: 'asset', action: 'create' },
  { resource: 'asset', action: 'update' },
  { resource: 'asset', action: 'dispose' },
  { resource: 'asset', action: 'approve' },
  { resource: 'maintenance_schedule', action: 'read' },
  { resource: 'maintenance_schedule', action: 'create' },
  { resource: 'maintenance_schedule', action: 'update' },
  { resource: 'maintenance_order', action: 'read' },
  { resource: 'maintenance_order', action: 'create' },
  { resource: 'maintenance_order', action: 'update' },
  { resource: 'maintenance_order', action: 'complete' },
  { resource: 'maintenance_order', action: 'cancel' },
  { resource: 'customer', action: 'read' },
  { resource: 'customer', action: 'create' },
  { resource: 'customer', action: 'update' },
  { resource: 'customer_merge_request', action: 'read' },
  { resource: 'customer_merge_request', action: 'decide' },
  { resource: 'consent', action: 'read' },
  { resource: 'consent', action: 'update' },
  { resource: 'feedback', action: 'read' },
  { resource: 'feedback', action: 'create' },
  { resource: 'feedback', action: 'update' },
  { resource: 'complaint', action: 'read' },
  { resource: 'complaint', action: 'create' },
  { resource: 'complaint', action: 'update' },
  // FASE 12 (migration 0050): Command Center & Reporting.
  { resource: 'report', action: 'run' },
  { resource: 'report', action: 'read' },
  { resource: 'metric', action: 'read' },
  { resource: 'metric', action: 'create' },
  { resource: 'metric', action: 'update' },
  // FASE 13 (migration 0052/0053): WhatsApp / Notifications.
  { resource: 'notification', action: 'request' },
  { resource: 'notification', action: 'read' },
  { resource: 'notification', action: 'retry' },
  { resource: 'notification', action: 'route_manage' },
  { resource: 'notification', action: 'template_manage' },
  { resource: 'notification', action: 'kill_switch_manage' },
  { resource: 'ai', action: 'query' },
  { resource: 'ai', action: 'read' },
  { resource: 'ai', action: 'approve' },
  { resource: 'ai', action: 'feedback' },
  { resource: 'migration', action: 'read' },
  { resource: 'migration', action: 'operate' },
  { resource: 'migration', action: 'approve' },
  { resource: 'migration', action: 'signoff' },
];

export function permissionKey(resource: PermissionResource, action: PermissionAction): string {
  return `${resource}.${action}`;
}
